import { memo, useMemo, useState } from "react";
import Close from "@zenith/assets/icons/Close";
import SearchIcon from "@zenith/assets/icons/SearchIcon";
import { OutletListDTO } from "@zenith/services/user";

export const ALL_OUTLETS_VALUE = "ALL";

export interface SelectOutletsPanelProps {
  outlets: OutletListDTO[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}

const SelectOutletsPanel = memo(function SelectOutletsPanel(
  props: SelectOutletsPanelProps
) {
  const { outlets, selectedIds, onChange, onClose } = props;
  const [search, setSearch] = useState("");

  const filteredOutlets = useMemo(
    () =>
      outlets.filter((o) =>
        (o.name ?? "").toLowerCase().includes(search.toLowerCase())
      ),
    [outlets, search]
  );

  const allSelected = outlets.length > 0 && selectedIds.length === outlets.length;

  const toggleAll = () => {
    onChange(allSelected ? [] : outlets.map((o) => o.id ?? ""));
  };

  const toggleOutlet = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((v) => v !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-[10px] border border-neutral-200 bg-white shadow-lg">
      <div className="flex items-center justify-between px-4 pt-4">
        <span className="text-base font-medium text-neutral-900">
          Select Outlet
        </span>
        <button type="button" onClick={onClose}>
          <Close width={16} height={16} />
        </button>
      </div>
      <div className="px-4 pt-3">
        <div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-3">
          <input
            type="text"
            placeholder="Search Outlet"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full text-sm outline-none placeholder:text-neutral-200"
          />
          <SearchIcon className="h-4 w-4 shrink-0 text-neutral-400" />
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto px-4 py-2">
        <label className="flex cursor-pointer items-center justify-between py-2.5 text-base text-neutral-600">
          All
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            className="h-5 w-5 accent-primary-300"
          />
        </label>
        {filteredOutlets.map((outlet) => (
          <label
            key={outlet.id}
            className="flex cursor-pointer items-center justify-between py-2.5 text-base text-neutral-600"
          >
            {outlet.name}
            <input
              type="checkbox"
              checked={selectedIds.includes(outlet.id ?? "")}
              onChange={() => toggleOutlet(outlet.id ?? "")}
              className="h-5 w-5 accent-primary-300"
            />
          </label>
        ))}
        {filteredOutlets.length === 0 && (
          <p className="py-4 text-center text-xs text-neutral-400">
            No outlet found.
          </p>
        )}
      </div>
    </div>
  );
});

export default SelectOutletsPanel;
